import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { classifyDay } from '../booking/daily';
import { loadBookings } from '../booking/loadBookings';
import { businessDate } from '../dates';
import { loadPendingFinance } from '../finance/loadPendingFinance';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { PageHead, shortDate } from '../ui/design';
import { Icon } from '../ui/icons';
import { Alert, Field, Loading, QueryFailure, readResponse } from '../ui/ui';

// The dashboard uses the same occupancy definition as the occupancy report
// and only the caller's permitted Branch. A failed load is No Data. A
// measured zero stays a zero (BR-REP-007).
const text: Dictionary<
  | 'welcome'
  | 'occupancy'
  | 'attention'
  | 'occupied'
  | 'eligible'
  | 'total'
  | 'maintenance'
  | 'openOccupancy'
  | 'pendingFinance'
  | 'movementToday'
  | 'todayRent'
  | 'todayPending'
  | 'todayHeld'
  | 'todayCash'
  | 'todayExpenses'
  | 'todayCompany'
  | 'openMovements'
  | 'openExpenses'
  | 'percent'
  | 'arrivals'
  | 'notApplicable'
  | 'notFixed'
  | 'noData'
  | 'branch'
  | 'archived'
  | 'incomplete'
  | 'scopeHint'
  | 'dashboard'
  | 'board'
  | 'newBooking'
  | 'checkIns'
  | 'checkOuts'
  | 'overstay'
  | 'todayLabel'
  | 'none'
  | 'money'
> = {
  en: {
    welcome: 'Welcome',
    occupancy: 'Occupancy',
    attention: 'Attention',
    occupied: 'Occupied units',
    eligible: 'Eligible units',
    total: 'Total units',
    maintenance: 'Maintenance units',
    openOccupancy: 'Open occupancy report',
    pendingFinance: 'Pending finance',
    movementToday: 'Today, transaction date',
    todayRent: 'Rent received',
    todayPending: 'Pending rent',
    todayHeld: 'Current deposit held',
    todayCash: 'Cash movement',
    todayExpenses: 'Branch expenses',
    todayCompany: 'Company expenses',
    openMovements: 'Open movement reports',
    openExpenses: 'Open expense report',
    percent: 'Occupancy',
    arrivals: 'Arrivals',
    notApplicable: 'There is no occupancy percentage because no unit is eligible.',
    notFixed: 'Exact ratio of eligible units (no approved rounding for a percentage)',
    noData: 'No Data',
    branch: 'Branch for occupancy and movements',
    archived: 'Archived',
    incomplete: 'Incomplete Data',
    scopeHint: 'Arrivals and pending tasks cover your permitted workflow scope.',
    dashboard: 'Dashboard',
    board: 'Unit board',
    newBooking: 'New booking',
    checkIns: 'Check-ins',
    checkOuts: 'Check-outs',
    overstay: 'Overstay',
    todayLabel: 'Today',
    none: 'None in this period.',
    money: 'Money',
  },
  ar: {
    welcome: 'مرحبًا',
    occupancy: 'الإشغال',
    attention: 'يتطلب الانتباه',
    occupied: 'الوحدات المشغولة',
    eligible: 'الوحدات المؤهلة',
    total: 'إجمالي الوحدات',
    maintenance: 'وحدات الصيانة',
    openOccupancy: 'فتح تقرير الإشغال',
    pendingFinance: 'المالية المعلقة',
    movementToday: 'اليوم، تاريخ الحركة',
    todayRent: 'الإيجار المقبوض',
    todayPending: 'إيجار معلّق',
    todayHeld: 'رصيد التأمين الحالي',
    todayCash: 'الحركة النقدية',
    todayExpenses: 'مصروفات الفرع',
    todayCompany: 'مصروفات الشركة',
    openMovements: 'فتح تقارير الحركة',
    openExpenses: 'فتح تقرير المصروفات',
    percent: 'الإشغال',
    arrivals: 'الوصول',
    notApplicable: 'لا توجد نسبة إشغال لأنه لا توجد وحدة مؤهلة.',
    notFixed: 'نسبة دقيقة من الوحدات المؤهلة (لا توجد قاعدة تقريب معتمدة للنسبة المئوية)',
    noData: 'لا توجد بيانات',
    branch: 'فرع الإشغال والحركات',
    archived: 'مؤرشف',
    incomplete: 'بيانات غير مكتملة',
    scopeHint: 'الوصول والمهام المعلقة ضمن نطاق العمل المسموح لك.',
    dashboard: 'لوحة المعلومات',
    board: 'لوحة الوحدات',
    newBooking: 'حجز جديد',
    checkIns: 'الدخول',
    checkOuts: 'الخروج',
    overstay: 'تجاوز المدة',
    todayLabel: 'اليوم',
    none: 'لا يوجد في هذه الفترة.',
    money: 'الأموال',
  },
};

function nextBusinessDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (day ?? 1) + 1)).toISOString().slice(0, 10);
}

const FINANCE_TASKS = [
  'finance.booking_view',
  'finance.pending_manage',
  'finance.noncash_verify',
  'finance.rent_payment_record',
  'finance.rent_refund',
  'finance.deposit_collection_record',
  'finance.deposit_refund',
  'finance.deposit_retained',
];

export function HomePage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const today = businessDate(new Date());
  const tomorrow = nextBusinessDate(today);
  const canBook = session.permissions.includes('booking.view');
  const canMovement = session.permissions.includes('finance.reports');
  const canExpense = session.permissions.includes('expense.report');
  const [selectedBranch, setSelectedBranch] = useState('');
  const branches = useQuery({
    queryKey: ['dashboard', 'branches', canBook ? 'operational' : 'financial'],
    enabled: canBook || canMovement || canExpense,
    queryFn: async ({ signal }) => {
      const response = canBook
        ? await client.listBranches({ query: { includeArchived: 'true' } }, { signal })
        : await client.listFinancialBranches({ signal });
      return readResponse(response).branches;
    },
  });
  const branchId = branches.data?.find((branch) => branch.id === selectedBranch)?.id ?? branches.data?.[0]?.id;
  const occupancy = useQuery({
    queryKey: ['dashboard', 'occupancy', branchId, today],
    enabled: canBook && Boolean(branchId),
    queryFn: async ({ signal }) => {
      if (!branchId) return null;
      const response = await client.currentOccupancy({ query: { branchId, asOf: today } }, { signal });
      return readResponse(response);
    },
  });
  const bookings = useQuery({
    queryKey: ['dashboard', 'bookings', today],
    enabled: canBook,
    queryFn: async ({ signal }) => {
      return loadBookings(client, {}, signal);
    },
  });
  const arrivals = bookings.data ? classifyDay(bookings.data, [], today).arrivals.length : null;
  const canFinanceTasks = FINANCE_TASKS.some((key) => session.permissions.includes(key));
  const pending = useQuery({
    queryKey: ['dashboard', 'pending-finance'],
    enabled: canFinanceTasks,
    queryFn: async ({ signal }) => {
      return loadPendingFinance(client, signal);
    },
  });
  const range = branchId ? { branchId, from: today, to: tomorrow } : null;
  const rent = useQuery({
    queryKey: ['dashboard', 'rent', branchId, today],
    enabled: canMovement && Boolean(range),
    queryFn: async ({ signal }) => {
      if (!range) return null;
      const response = await client.rentMovement({ query: range }, { signal });
      return readResponse(response);
    },
  });
  const deposits = useQuery({
    queryKey: ['dashboard', 'deposits', branchId, today],
    enabled: canMovement && Boolean(range),
    queryFn: async ({ signal }) => {
      if (!range) return null;
      const response = await client.depositMovement({ query: range }, { signal });
      return readResponse(response);
    },
  });
  const cash = useQuery({
    queryKey: ['dashboard', 'cash', branchId, today],
    enabled: canMovement && Boolean(range),
    queryFn: async ({ signal }) => {
      if (!range) return null;
      const response = await client.cashMovement({ query: range }, { signal });
      return readResponse(response);
    },
  });
  const expenses = useQuery({
    queryKey: ['dashboard', 'expenses', branchId, today],
    enabled: canExpense && Boolean(range),
    queryFn: async ({ signal }) => {
      if (!range) return null;
      const response = await client.expenseMovement({ query: range }, { signal });
      return readResponse(response);
    },
  });
  const rentData = rent.isError ? null : rent.data;
  const depositData = deposits.isError ? null : deposits.data;
  const cashData = cash.isError ? null : cash.data;
  const hasMovementData = Boolean(rentData || depositData || cashData);
  const incompleteMovement =
    hasMovementData && (!rentData || !depositData || !cashData || rentData.pendingRent === undefined);
  const day = bookings.data ? classifyDay(bookings.data, [], today) : null;
  const stat = (label: string, value: string | number, tone?: 'bad') => (
    <p className={tone === 'bad' ? 'er-stat er-stat--bad' : 'er-stat'}>
      <span className="er-stat__label">
        {label}
        <span className="er-visually-hidden">: </span>
      </span>
      <span className="er-stat__value">{value}</span>
    </p>
  );
  const movementRow = (label: string, value: string) => (
    <p className="er-stat">
      <span className="er-stat__label">
        {label}
        <span className="er-visually-hidden">: </span>
      </span>
      <span className="er-stat__value" dir="ltr">
        {value}
      </span>
    </p>
  );
  const moveList = (
    rows: readonly { id: string; displayReference: string; start: string; end: string }[],
    when: 'start' | 'end',
  ) =>
    rows.length === 0 ? (
      <div className="er-empty">{t.none}</div>
    ) : (
      <div className="er-list">
        {rows.map((booking) => (
          <Link key={booking.id} className="er-row" to={`/bookings/${booking.id}`}>
            <span className="er-row__when">{shortDate(locale, booking[when])}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="er-cell-main er-mono" style={{ display: 'block' }}>
                {booking.displayReference}
              </span>
              <span className="er-cell-sub" style={{ display: 'block' }}>
                {booking.start} → {booking.end}
              </span>
            </span>
            <Icon name="chev-right" className="er-flip er-chev" />
          </Link>
        ))}
      </div>
    );
  return (
    <main>
      <PageHead
        title={t.dashboard}
        sub={`${t.welcome}, ${session.displayName} · ${today}`}
        actions={
          canBook ? (
            <>
              <Link className="er-btn er-btn--secondary" to="/board">
                <Icon name="board" />
                {t.board}
              </Link>
              {session.permissions.includes('booking.create') ? (
                <Link className="er-btn er-btn--primary" to="/bookings">
                  <Icon name="plus" />
                  {t.newBooking}
                </Link>
              ) : null}
            </>
          ) : null
        }
      />
      {canBook || canMovement || canExpense ? (
        <div className="er-tools">
          <QueryFailure query={branches} />
          <Field id="dashboard-branch" label={t.branch}>
            <select
              id="dashboard-branch"
              value={branchId ?? ''}
              style={{ minWidth: 260 }}
              disabled={branches.isPending || branches.isError}
              onChange={(event) => setSelectedBranch(event.target.value)}
            >
              {!branchId && <option value="" />}
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                  {branch.archived ? ` (${t.archived})` : ''}
                </option>
              ))}
            </select>
          </Field>
        </div>
      ) : null}
      {canBook ? (
        <section className="er-panel er-section-block">
          <div className="er-panel__head">
            <h2 className="er-panel__title">{t.occupancy}</h2>
            <span className="er-spacer" />
            {occupancy.data ? (
              <Link className="er-link" to={`/reports?branchId=${branchId ?? ''}&asOf=${today}`}>
                {t.openOccupancy}
              </Link>
            ) : null}
          </div>
          <div className="er-panel__body">
            {branches.isPending || (branchId && occupancy.isPending) ? (
              <Loading />
            ) : branches.isError || occupancy.isError || !occupancy.data ? (
              <p className="er-empty">{t.noData}</p>
            ) : (
              <div className="er-money">
                {stat(t.total, occupancy.data.operationalUnits)}
                {stat(t.occupied, occupancy.data.occupiedUnits)}
                {stat(t.maintenance, occupancy.data.maintenanceUnits)}
                {stat(t.eligible, occupancy.data.eligibleUnits)}
                {occupancy.data.eligibleUnits === 0 ? (
                  <p className="er-callout">{t.notApplicable}</p>
                ) : occupancy.data.occupancyPercent !== null ? (
                  <div className="er-stat">
                    <p
                      className="er-stat__label"
                      style={{ margin: 0 }}
                    >{`${t.percent}: ${occupancy.data.occupancyPercent}%`}</p>
                    <span className="er-meter" aria-hidden="true">
                      <i style={{ width: `${Math.min(100, Number(occupancy.data.occupancyPercent))}%` }} />
                    </span>
                  </div>
                ) : (
                  <div className="er-stat">
                    <span className="er-stat__label">{t.percent}</span>
                    <span className="er-stat__value">{`${occupancy.data.occupiedUnits} / ${occupancy.data.eligibleUnits}`}</span>
                    <span className="er-stat__sub">{t.notFixed}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      ) : null}
      {canBook ? (
        <div className="er-grid3">
          <section className="er-panel">
            <div className="er-panel__head">
              <h2 className="er-panel__title">{t.checkIns}</h2>
              <span className="er-spacer" />
              <span className="er-chip">{t.todayLabel}</span>
            </div>
            {bookings.isPending ? (
              <Loading />
            ) : day ? (
              moveList(day.arrivals, 'start')
            ) : (
              <p className="er-empty">{t.noData}</p>
            )}
          </section>
          <section className="er-panel">
            <div className="er-panel__head">
              <h2 className="er-panel__title">{t.checkOuts}</h2>
              <span className="er-spacer" />
              <span className="er-chip">{t.todayLabel}</span>
            </div>
            {bookings.isPending ? (
              <Loading />
            ) : day ? (
              moveList(day.departures, 'end')
            ) : (
              <p className="er-empty">{t.noData}</p>
            )}
          </section>
          <section className="er-panel">
            <div className="er-panel__head">
              <h2 className="er-panel__title">{t.overstay}</h2>
            </div>
            {bookings.isPending ? (
              <Loading />
            ) : day ? (
              moveList(day.overstay, 'end')
            ) : (
              <p className="er-empty">{t.noData}</p>
            )}
          </section>
        </div>
      ) : null}
      {canBook || canFinanceTasks || canMovement || canExpense ? (
        <section className="er-panel er-section-block">
          <div className="er-panel__head">
            <h2 className="er-panel__title">{t.attention}</h2>
          </div>
          <div className="er-panel__body">
            {(canBook || canFinanceTasks) && <p className="er-hint">{t.scopeHint}</p>}
            <div className="er-attention">
              {canBook ? (
                bookings.isPending ? (
                  <Loading />
                ) : bookings.isError || arrivals === null ? (
                  <p>{t.noData}</p>
                ) : (
                  <p>
                    <Link className="er-att" to="/operations">{`${t.arrivals}: ${arrivals}`}</Link>
                  </p>
                )
              ) : null}
              {canFinanceTasks ? (
                pending.isPending ? (
                  <Loading />
                ) : pending.isError || !pending.data ? (
                  <>
                    <p>{t.noData}</p>
                    <QueryFailure query={pending} />
                  </>
                ) : (
                  <p>
                    <Link className="er-att" to="/finance">{`${t.pendingFinance}: ${pending.data.length}`}</Link>
                  </p>
                )
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
      {canMovement || canExpense ? (
        <section className="er-panel er-section-block">
          <div className="er-panel__head">
            <h2 className="er-panel__title">{t.money}</h2>
          </div>
          <div className="er-panel__body">
            {canMovement ? (
              branches.isPending || (range && (rent.isPending || deposits.isPending || cash.isPending)) ? (
                <Loading />
              ) : (
                <>
                  {!range || !hasMovementData ? (
                    <p>{t.noData}</p>
                  ) : (
                    <>
                      {incompleteMovement && <Alert kind="info">{t.incomplete}</Alert>}
                      <p className="er-overline">{t.movementToday}</p>
                      <div className="er-money">
                        {movementRow(t.todayRent, rentData?.rentReceived ?? t.noData)}
                        {movementRow(t.todayPending, rentData?.pendingRent ?? t.noData)}
                        {movementRow(t.todayHeld, depositData?.depositHeld ?? t.noData)}
                        {movementRow(t.todayCash, cashData?.cashMovement ?? t.noData)}
                      </div>
                      <p>
                        <Link to={`/reports?branchId=${branchId ?? ''}&from=${today}&to=${tomorrow}`}>
                          {t.openMovements}
                        </Link>
                      </p>
                    </>
                  )}
                  <QueryFailure query={rent} />
                  <QueryFailure query={deposits} />
                  <QueryFailure query={cash} />
                </>
              )
            ) : null}
            {canExpense ? (
              branches.isPending || (range && expenses.isPending) ? (
                <Loading />
              ) : expenses.isError || !expenses.data ? (
                <p>{t.noData}</p>
              ) : (
                <>
                  <div className="er-money">
                    {movementRow(t.todayExpenses, expenses.data.branchExpenses)}
                    {expenses.data.companyExpenses !== null
                      ? movementRow(t.todayCompany, expenses.data.companyExpenses)
                      : null}
                  </div>
                  <p>
                    <Link to={`/reports?branchId=${branchId ?? ''}&from=${today}&to=${tomorrow}`}>
                      {t.openExpenses}
                    </Link>
                  </p>
                </>
              )
            ) : null}
          </div>
        </section>
      ) : null}
      {!canBook && !canFinanceTasks && !canMovement && !canExpense ? <p>{t.noData}</p> : null}
    </main>
  );
}
